import {
  forwardRef,
  InputHTMLAttributes,
  ReactElement,
  ReactNode,
  Ref,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { classNames } from "@/utils/classNames";
import { Icon, IconProps } from "./Icon";
import { observer } from "mobx-react";
import { handleEnter } from "@/utils/keyboardEvents";
import { deferred, when } from "@/utils/functions";
import { isIconName } from "./iconHelpers";
import { IconButton } from "./Button";
import { mergeRefs, useElementId } from "@/utils/reactHelpers";
import { FocusTooltip } from "./Tooltip";
import { isTMessage, TMessage, TMessageParam } from "@/i18n/tt";
import {
  DCChangeEventHandler,
  DCClipboardEventHandler,
  DCFocusEventHandler,
  DCKeyboardEventHandler,
  useChangeEventHandler,
  useClipboardEventHandler,
  useFocusEventHandler,
  useKeyboardEventHandler,
  useLanguage,
  useTranslation,
} from "@/stores/RootStoreContext";
import { NumberFieldModel, StringFieldModel } from "@/common/commonForms";
import { TableContext } from "./TableContext";

export type TextInputProps = {
  id?: string;
  disabled?: boolean;
  error?: boolean;
  icon?: ReactElement | IconProps["name"];
  addon?: ReactElement;
  addonPosition?: "left" | "right";
  prefix?: string;
  label?: string | TMessage | ReactNode;
  message?: string;
  messageVariant?: "default" | "tooltip";
  name?: string;
  onChange?: DCChangeEventHandler<HTMLInputElement>;
  onFocus?: DCFocusEventHandler<HTMLElement>;
  onBlur?: DCFocusEventHandler<HTMLElement>;
  onKeyDown?: DCKeyboardEventHandler<HTMLInputElement>;
  onPaste?: DCClipboardEventHandler<HTMLInputElement>;
  placeholder?: string | TMessage;
  value?: string | number;
  autofocus?: boolean;
  autoSelect?: boolean;
  inputRef?: Ref<HTMLInputElement>;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
  readOnly?: boolean;
  type?: "text" | "password" | "number" | "search";
  inline?: boolean;
  align?: "left" | "center" | "right";
  variant?: "in-table";
  compact?: boolean;
  testId?: string;
  autoComplete?: string;
  className?: string;
};

export const TextInput = forwardRef<HTMLDivElement, TextInputProps>(function TextInput(
  {
    id,
    disabled,
    error,
    icon,
    addon,
    addonPosition = "right",
    prefix,
    label,
    message,
    messageVariant = "default",
    name,
    placeholder,
    value,
    autofocus,
    autoSelect,
    inputRef,
    inputProps,
    readOnly,
    type,
    inline,
    align = "left",
    variant,
    testId,
    autoComplete = "off",
    className,
    ...props
  },
  ref,
) {
  const { t } = useTranslation();
  const [passwordVisible, setPasswordVisible] = useState<boolean>(false);
  const inputId = useElementId(id);
  const tableContext = useContext(TableContext);
  const inTable = variant === "in-table" || tableContext?.editable;
  const compact = props.compact || tableContext?.compact;
  const internalInputRef = useRef<HTMLInputElement>(null);
  const focusHandler = useFocusEventHandler(props.onFocus);
  const blurHandler = useFocusEventHandler(props.onBlur);
  const changeHandler = useChangeEventHandler(props.onChange);
  const keyDownHandler = useKeyboardEventHandler(props.onKeyDown);
  const pasteHandler = useClipboardEventHandler(props.onPaste);

  const renderInput = (tooltipTargetRef?: (el: HTMLElement | null) => void) => (
    <input
      id={inputId}
      ref={mergeRefs(internalInputRef, inputRef, tooltipTargetRef)}
      disabled={disabled}
      type={passwordVisible ? "text" : (type ?? "text")}
      name={name}
      placeholder={isTMessage(placeholder) ? t(placeholder) : placeholder}
      value={value}
      onChange={changeHandler}
      onFocus={(event) => {
        if (autoSelect) {
          internalInputRef.current?.select();
        }
        focusHandler?.(event);
      }}
      onBlur={blurHandler}
      onKeyDown={keyDownHandler}
      onPaste={pasteHandler}
      autoFocus={autofocus}
      readOnly={readOnly}
      data-testid={testId}
      autoComplete={autoComplete}
      {...inputProps}
      className={classNames(
        inputProps?.className,
        "text-input--input",
        "flex-1",
        "focus-visible:outline-hidden",
        compact
          ? "p-1"
          : [
              "py-3",
              icon || prefix ? "pl-2" : "pl-4",
              type !== "password" && !(addon && addonPosition === "right") && "pr-4",
            ],
        "w-full",
        "bg-transparent",
        "disabled:bg-transparent",
        align === "center" ? "text-center" : align === "right" ? "text-right" : "text-left",
      )}
    />
  );

  return (
    <div className={classNames(!inline && "grow", className)}>
      {label && (
        <label htmlFor={inputId} className={classNames("block mb-1", inTable && "sr-only")}>
          {isTMessage(label) ? t(label) : label}
        </label>
      )}
      <div
        ref={ref}
        className={classNames(
          "relative",
          inline ? "inline-flex w-60" : "flex w-full min-w-[10ch]",
          "grow",
          inTable && "w-full",
          "items-center",
          "leading-tight",
          inTable
            ? "has-[.text-input--input:focus]:ring-2"
            : ["has-[.text-input--input:focus]:ring-1", "border rounded-sm"],
          "cursor-text",
          error
            ? [
                "color-default",
                "bg-default",
                "border-error",
                "focus-within:border-error",
                "focus-within:ring-error",
                "hover:border-error",
                "hover:focus-within:border-error",
                inTable && [
                  "before:left-0",
                  "before:right-0",
                  "before:bottom-0",
                  "before:absolute",
                  "before:border-error",
                  "before:border-b-2",
                  "focus-within:before:border-none",
                ],
              ]
            : disabled
              ? ["subtle", "bg-gray-x-soft", "border-gray-soft", "hover:border-gray-soft"]
              : [
                  "color-default",
                  "bg-default",
                  "border-gray",
                  "has-[.text-input--input:focus]:border-primary",
                  "has-[.text-input--input:focus]:ring-primary",
                  "hover:border-gray-hard",
                ],
        )}
      >
        {addonPosition === "left" && addon}
        {icon && (
          <div
            className={classNames(
              "text-xl",
              "leading-5",
              "h-5",
              "subtle",
              compact ? "ml-1" : "ml-4",
              disabled && "pointer-events-none",
            )}
          >
            {isIconName(icon) ? <Icon name={icon} /> : icon}
          </div>
        )}
        {prefix && (
          <div className={classNames("flex-none", compact ? "ml-1" : icon ? "ml-2" : "ml-4")}>
            {prefix}
          </div>
        )}
        {inTable || messageVariant === "tooltip" ? (
          <FocusTooltip content={message} color={error ? "error" : undefined} placement={"top"}>
            {({ targetRef }) => renderInput(targetRef)}
          </FocusTooltip>
        ) : (
          renderInput()
        )}
        {addonPosition !== "left" && type === "password" && (
          <IconButton
            icon={passwordVisible ? "eye-closed" : "eye"}
            tooltip={passwordVisible ? t("hide-password") : t("show-password")}
            onClick={() => setPasswordVisible(!passwordVisible)}
            compact={compact}
          />
        )}
        {addonPosition === "right" && addon}
      </div>
      {message && messageVariant === "default" && !inTable && (
        <div className={classNames("pt-1", "pl-4", "text-sm", error ? "color-error" : "subtle")}>
          {message}
        </div>
      )}
    </div>
  );
});

export type TextInputViewProps = {
  model: StringFieldModel | NumberFieldModel;
  align?: "left" | "center" | "right";
  displayValue?: () => TMessage | TMessageParam | string | undefined;
};

export const TextInputView = observer(function TextInputView({
  model,
  align = "left",
  displayValue,
}: TextInputViewProps) {
  const { t, tf, lang } = useTranslation();
  const tableContext = useContext(TableContext);
  const compact = tableContext?.compact;
  const error = !model.isValid;
  const value = displayValue?.() ?? model.inputValue;

  useEffect(() => {
    if ("setLocale" in model) {
      model.setLocale(lang);
    }
  }, [model, lang]);

  return (
    <div
      tabIndex={0}
      className={classNames(
        "flex w-full items-center leading-tight",
        "relative outline-hidden",
        error && [
          "before:left-0",
          "before:right-0",
          "before:bottom-0",
          "before:absolute",
          "before:border-error",
          "before:border-b-2",
        ],
      )}
      data-tooltip={model.error ? t(model.error) : undefined}
      data-tooltip-focus
      data-tooltip-color={"error"}
    >
      <div
        className={classNames(
          "flex-1 truncate",
          compact ? "p-1" : ["py-3", "pl-4", "pr-4"],
          align === "center" ? "text-center" : align === "right" ? "text-right" : "text-left",
        )}
      >
        {tf(value) || <>&nbsp;</>}
      </div>
    </div>
  );
});

export type MTextInputProps = {
  model: StringFieldModel | NumberFieldModel;
} & Omit<TextInputProps, "value">;

export const MTextInput = observer(function MTextInput({
  model,
  onChange,
  onBlur,
  onPaste,
  onKeyDown,
  ...props
}: MTextInputProps) {
  const { t } = useTranslation();
  const locale = useLanguage();

  useEffect(() => {
    if ("setLocale" in model) {
      model.setLocale(locale);
    }
  }, [locale, model]);

  return (
    <TextInput
      {...props}
      inputRef={model.setRef}
      value={model.inputValue}
      onChange={onChange ?? model.setInputValue}
      onBlur={onBlur ?? model.commit}
      onKeyDown={onKeyDown ?? handleEnter(model.commit)}
      onPaste={onPaste ?? deferred(model.commit)}
      error={props.error ?? !model.isValid}
      message={props.message ?? when(model.error, t)}
    />
  );
});
